import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";
import { object, string } from "yup";

import Button from "components/Button";
import DataTable from "components/DataTable";
import Input from "components/Input";
import Modal from "components/Modal";
import { ExclamationSVGIcon } from "components/SVGIcons";
import { routes } from "constants/routes";
import { BILLING_TABLE_HEADER_LIST } from "constants/user-settings";
import { formatNumber } from "helpers/formatNumber";
import { PlanDetails, SubscriptionPlanType } from "helpers/subscriptionPlans";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import useStripeScript from "hooks/useStripeScript";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { IBillingTableList, StripeSubscription, SubscriptionHistory } from "models/UserSetting";
import {
  deleteSubscriptionHistoryAPI,
  sendSubscriptionFeedbackAPI
} from "services/api/userSetting";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import { getStripeSubscription, getSubscriptionHistory } from "store/userSetting/actions";
import { selectStripeSubscribeStatus } from "store/userSetting/selector";
import SubscriptionPlanCard from "../modules/SubscriptionPlanCard";
import BillingHeader from "./BillingHeader";
import BillingStripeContent from "./BillingStripeContent";
import FullAccessSection from "./FullAccess";
import StripeCheckoutButton from "./StripeCheckoutButton";
import styles from "./style.module.scss";

export const DeactivateAccountFormSchema = object({
  feedback: string().required("This field is required")
});

export default function UserBilling() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDesktop } = useDeviceType();
  const { isFactoringApproved } = useUserFeature();
  const [subscriptionPlanType, setSubscriptionPlanType] =
    useState<SubscriptionPlanType>("annually");
  const [planDetails, setPlanDetails] = useState<PlanDetails>();
  const stripeSubscriptionStatus = useSelector(selectStripeSubscribeStatus);
  const authState = useSelector((state: RootState) => state.authState);
  const accountType = useMemo(() => authState?.account?.type ?? "", [authState]);

  const { stripeSubscription: hasSubscription } = useSelector(
    (state: RootState) => state.userSettingState
  );

  const { plan_type: planType, amount: planAmount = "0" } = useMemo<
    Partial<StripeSubscription>
  >(() => {
    if (hasSubscription) {
      return hasSubscription;
    } else return {};
  }, [hasSubscription]);

  const [isDeactivateConfirmationModalOpen, toggleDeactivateConfirmationModalOpen] = useToggle();
  const { handleStripeCheckout } = useStripeScript({
    plan: planType ?? "monthly",
    planAmount: (Number(planAmount || 0) / 100).toFixed(2)
  });

  const hasFullAccess = isFactoringApproved === "approved";
  const tableData: IBillingTableList[] = Object.values(stripeSubscriptionStatus.history);

  useEffect(() => {
    hasSubscription && dispatch(getSubscriptionHistory());
  }, [hasSubscription]);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit
  } = useForm({
    resolver: yupResolver(DeactivateAccountFormSchema)
  });

  const deactivateSubscription = async ({ feedback }: { feedback: string }) => {
    toggleDeactivateConfirmationModalOpen();
    try {
      dispatch(handleLoading(true));
      await sendSubscriptionFeedbackAPI({ message: feedback });
      await deleteSubscriptionHistoryAPI().finally(() => handleLoading(false));
      dispatch(getStripeSubscription());
      dispatch(getSubscriptionHistory());
      showSuccessMessage("Success deactivating account");
    } catch (error) {
      showErrorMessage("Error deactivating account");
    } finally {
      dispatch(handleLoading(false));
    }
  };

  const goReceipt = (data: SubscriptionHistory) => {
    navigate(routes.SETTINGS_BILLING_RECEIPT, { state: { data } });
  };

  return (
    <MainLayout
      desktopTitle="Billing"
      customHeader={<BillingHeader />}
      hideNavigation
      className={styles.billingContainer}>
      {hasFullAccess ? <FullAccessSection /> : null}

      {!hasFullAccess ? (
        <>
          {stripeSubscriptionStatus.info ? (
            <h4 className={styles.currentPlanLabel}>Current Plan</h4>
          ) : null}

          <SubscriptionPlanCard
            onChangeSubscriptionPlanType={(plan: SubscriptionPlanType) =>
              setSubscriptionPlanType(plan)
            }
            onHandleDeactivatePlan={toggleDeactivateConfirmationModalOpen}
            onHandleUpdatePlan={handleStripeCheckout}
            setPlanDetails={setPlanDetails}
          />

          {stripeSubscriptionStatus.info ? (
            <Button
              rounded
              className="flex-none h-14 desktop:hidden"
              onClick={handleStripeCheckout}>
              UPDATE BILLING INFO
            </Button>
          ) : null}
        </>
      ) : null}

      {stripeSubscriptionStatus.history.length ? (
        <div className={styles.history}>
          <h4 className="text-xl font-medium">Past Billing History</h4>

          <table className={styles.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th className="!text-right">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {(stripeSubscriptionStatus.history as SubscriptionHistory[])?.map((item) => (
                <tr key={item.created}>
                  <td>{dayjs.unix(item.created).format("MM/DD/YYYY")}</td>
                  <td>{formatNumber(item.amount / 100, 2)}</td>
                  <td className="!text-right">
                    <button onClick={() => goReceipt(item)} className="underline text-info">
                      View Receipt
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <DataTable
            data={tableData.map((data, index) => ({
              ...data,
              receipt: { action: () => goReceipt(stripeSubscriptionStatus.history[index]) }
            }))}
            headers={BILLING_TABLE_HEADER_LIST}
            className="mobile:hidden"
          />
        </div>
      ) : null}
      {!hasFullAccess && !stripeSubscriptionStatus.info && planDetails && (
        <BillingStripeContent
          subscriptionPlanType={subscriptionPlanType}
          planDetails={planDetails}
        />
      )}

      {!hasFullAccess && stripeSubscriptionStatus.info ? (
        <div className="flex flex-none gap-3 mt-auto desktop:hidden desktop:ml-auto">
          <StripeCheckoutButton
            label="Update Billing Info"
            accountType={accountType}
            plan={subscriptionPlanType}
            disabled={false}
            className="rounded-full h-14 mobile:hidden w-fit desktop:text-base"
          />
          StripeCheckoutButton
          <Button
            className="!bg-error h-14 mobile:flex-1"
            rounded
            onClick={toggleDeactivateConfirmationModalOpen}>
            {isDesktop ? "Deactivate My Account" : "DEACTIVATE MY ACCOUNT"}
          </Button>
        </div>
      ) : null}

      <Modal
        isOpen={isDeactivateConfirmationModalOpen}
        closeModal={toggleDeactivateConfirmationModalOpen}
        wrapperClass="!my-auto desktop:!min-h-fit desktop:w-1/2 text-sm"
        dialogClass="!bg-foreground !px-3 !py-4 !border !border-card !overflow-y-auto gap-4"
        closeClass="!top-24 !right-6 !bg-transparent !text-placeholder">
        <form className="flex flex-col gap-4" onSubmit={handleSubmit(deactivateSubscription)}>
          <div className="flex items-center gap-1 text-xl font-bold text-primary">
            <ExclamationSVGIcon />
            <span>Confirm</span>
          </div>
          <div className="flex flex-col gap-3 px-1 text-text">
            <p>
              Are you sure you want to deactivate the subscription on your account? Please note that
              the monthly subscription price is now $29.00 per month. Cancelling now means you might
              lose your current pricing in the future.{" "}
            </p>
            <div className="flex flex-col gap-1">
              <span className="font-bold">Please enter a reason for why you’re cancelling</span>
              <Input
                name="feedback"
                className="w-full"
                register={register}
                error={errors.feedback}
              />
            </div>
          </div>
          <div className="flex gap-4 justify-end mt-1 pr-1 *:w-20 *:text-sm *:rounded-full *:h-9">
            <Button onClick={toggleDeactivateConfirmationModalOpen} variant="outline">
              Cancel
            </Button>
            <Button type="submit" className="!w-fit" loading={isSubmitting}>
              Deactivate Account
            </Button>
          </div>
        </form>
      </Modal>
    </MainLayout>
  );
}
